import { lazy, Suspense } from 'react';
import { PermissionGuard } from '@emfood/shared';
import { Outlet, createRoute, SearchSchemaInput } from '@tanstack/react-router';

import { PERMISSIONS } from 'src/api/roles-config';
import DashboardLayout from 'src/layouts/dashboard';

import AppAuthGuard from 'src/components/auth/app-auth-guard';
import { SplashScreen, LoadingScreen } from 'src/components/loading-screen';

import { CsvImportsListSearchParamsSchema } from 'src/sections/products-bulk-actions/templates/search-params';

import { rootRoute } from './root';

const CsvTemplatesImportsListView = lazy(
  () => import('src/sections/products-bulk-actions/templates/csv-imports-list-view')
);
const CsvTemplateImportDetailsView = lazy(
  () => import('src/sections/products-bulk-actions/templates/csv-import-details-view')
);
const PriceCsvImportsListView = lazy(
  () => import('src/sections/products-bulk-actions/prices/price-csv-imports-list-view')
);
const CsvPriceImportDetailsView = lazy(
  () => import('src/sections/products-bulk-actions/prices/csv-price-import-details-view')
);
const BespokePriceCsvImportsListView = lazy(
  () =>
    import('src/sections/products-bulk-actions/bespoke-prices/bespoke-price-csv-imports-list-view')
);
const CsvBespokePriceImportDetailsView = lazy(
  () =>
    import(
      'src/sections/products-bulk-actions/bespoke-prices/csv-bespoke-price-import-details-view'
    )
);

const productsBulkActionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'suppliers/$supplierOrgId/bulk-actions',
  component: () => (
    <AppAuthGuard splashScreen={<SplashScreen />}>
      <PermissionGuard requiredPermissions={[PERMISSIONS.DISTRIBUTORS_READ_CSV_TEMPLATE_IMPORTS]}>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </PermissionGuard>
    </AppAuthGuard>
  ),
});

const productsBulkActionsIndexRoute = createRoute({
  getParentRoute: () => productsBulkActionsRoute,
  path: '/',
  component: CsvTemplatesImportsListView,
  validateSearch: (search: unknown & SearchSchemaInput) =>
    CsvImportsListSearchParamsSchema.validateSync(search),
});

const csvImportDetailsRoute = createRoute({
  getParentRoute: () => productsBulkActionsRoute,
  path: '$csvImportId',
  component: CsvTemplateImportDetailsView,
});

const priceBulkActionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'suppliers/$supplierOrgId/price-bulk-actions',
  component: () => (
    <AppAuthGuard splashScreen={<SplashScreen />}>
      <PermissionGuard requiredPermissions={[PERMISSIONS.DISTRIBUTORS_READ_CSV_PRICE_IMPORTS]}>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </PermissionGuard>
    </AppAuthGuard>
  ),
});

const priceBulkActionsIndexRoute = createRoute({
  getParentRoute: () => priceBulkActionsRoute,
  path: '/',
  component: PriceCsvImportsListView,
  validateSearch: (search: unknown & SearchSchemaInput) =>
    CsvImportsListSearchParamsSchema.validateSync(search),
});

const priceCsvImportDetailsRoute = createRoute({
  getParentRoute: () => priceBulkActionsRoute,
  path: '$csvImportId',
  component: CsvPriceImportDetailsView,
});

const bespokePriceBulkActionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'suppliers/$supplierOrgId/bespoke-price-bulk-actions',
  component: () => (
    <AppAuthGuard splashScreen={<SplashScreen />}>
      <PermissionGuard
        requiredPermissions={[PERMISSIONS.DISTRIBUTORS_READ_CSV_BESPOKE_PRICE_IMPORTS]}
      >
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </PermissionGuard>
    </AppAuthGuard>
  ),
});

const bespokePriceBulkActionsIndexRoute = createRoute({
  getParentRoute: () => bespokePriceBulkActionsRoute,
  path: '/',
  component: BespokePriceCsvImportsListView,
  validateSearch: (search: unknown & SearchSchemaInput) =>
    CsvImportsListSearchParamsSchema.validateSync(search),
});

const bespokePriceCsvImportDetailsRoute = createRoute({
  getParentRoute: () => bespokePriceBulkActionsRoute,
  path: '$csvImportId',
  component: CsvBespokePriceImportDetailsView,
});

export const productsBulkActionsRouteTree = productsBulkActionsRoute.addChildren([
  productsBulkActionsIndexRoute,
  csvImportDetailsRoute,
]);

export const priceBulkActionsRouteTree = priceBulkActionsRoute.addChildren([
  priceBulkActionsIndexRoute,
  priceCsvImportDetailsRoute,
]);

export const bespokePriceBulkActionsRouteTree = bespokePriceBulkActionsRoute.addChildren([
  bespokePriceBulkActionsIndexRoute,
  bespokePriceCsvImportDetailsRoute,
]);
